import React, { useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import AnnotationsModule from "highcharts/modules/annotations";
import {
  Box,
  Button,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useStyles } from "../../../theme/appStyles";
import { IProductPriceSplineChart } from "../../../Api/Interface/api.interface";
import dayjs from "dayjs";

AnnotationsModule(Highcharts);

interface IProductPriceSplineChartRecord {
  data: IProductPriceSplineChart[];
}

const ProductPriceSplineChart: React.FC<IProductPriceSplineChartRecord> = ({
  data,
}) => {
  const classes = useStyles();
  const [showTable, setShowTable] = useState(false);

  // Parse data for chart
  const parsedSeries: Highcharts.SeriesSplineOptions[] = (data || []).map(
    (series) => ({
      type: "spline",
      name: series.name,
      data: (series.data || []).map(([dateString, value]) => [
        new Date(dateString).getTime(),
        value,
      ]),
    })
  );

  // Get all unique timestamps for the table
  const allTimestampsSet = new Set<string>();
  data.forEach((series) => {
    series.data.forEach(([dateString]) => {
      allTimestampsSet.add(dateString);
    });
  });
  const allTimestamps = Array.from(allTimestampsSet).sort();

  // Build table rows
  const tableRows = allTimestamps.map((timestamp) => {
    const row: Record<string, any> = { timestamp };
    data.forEach((series) => {
      const found = series.data.find(([date]) => date === timestamp);
      row[series.name] = found ? found[1] : "-";
    });
    return row;
  });

  const options: Highcharts.Options = {
    chart: {
      type: "spline",
      style: {
        fontFamily: '"Play", sans-serif',
      },
    },
    title: {
      useHTML: true,
      text: `<div class="${classes.h4Quoto}">Product Price Trends by Supplier (Spline Chart)</div>`,
    },
    xAxis: {
      type: "datetime",
      title: {
        text: `<div class="${classes.h5}">Date & Time</div>`,
      },
      labels: {
        format: "{value:%Y-%m-%d %I:%M %p}",
      },
    },
    yAxis: {
      title: {
        text: `<div class="${classes.h5}">Price ($)</div>`,
      },
    },
    tooltip: {
      shared: true,
      xDateFormat: "%Y-%m-%d %H:%M:%S",
      // crosshairs: true,
    },
    plotOptions: {
      spline: {
        marker: {
          enabled: true,
        },
      },
    },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },
    series: parsedSeries,
  };

  return (
    <Box>
      <TableContainer>
        <Box mb={2} textAlign="right">
          {data.length > 0 ? (
            <Box>
              <div style={{ minWidth: "650px" }}>
                <HighchartsReact highcharts={Highcharts} options={options} />
              </div>
              <Typography
                onClick={() => setShowTable(!showTable)}
                sx={{
                  cursor: "pointer",
                  textAlign: "center",
                }}
                variant="h6"
                gutterBottom
              >
                {showTable ? "Hide Table View" : "View Table"}
              </Typography>
            </Box>
          ) : (
            <Typography>No chart data available.</Typography>
          )}

          {showTable && (
            <Box>
              <TableContainer>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          background: "#fafafa",
                          minWidth: "200px",
                        }}
                      >
                        <strong>Date & Time</strong>
                      </TableCell>
                      {data.map((series, idx) => (
                        <TableCell
                          key={idx}
                          align="right"
                          sx={{
                            fontWeight: "bold",
                            background: "#fafafa",
                            minWidth: "150px",
                          }}
                        >
                          <strong>{series.name}</strong>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          {dayjs(row.timestamp).format("YYYY-MM-DD hh:mm A")}
                        </TableCell>
                        {data.map((series, sIdx) => (
                          <TableCell
                            key={sIdx}
                            align="right"
                            sx={{ position: "relative", minWidth: "150px" }}
                          >
                            {row[series.name] !== "-"
                              ? Number(row[series.name]).toFixed(2)
                              : "-"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </TableContainer>
    </Box>
  );
};

export default ProductPriceSplineChart;
